/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2023-01-03 15:58:53
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { Grid } from '@shopify/polaris';
import { withI18n, WithI18nProps } from '@shopify/react-i18n';
import Filter from 'src/components/containers/analytics-v2/filter';
import Chart from 'src/components/views/analytics-v2/display/chart';
import AnalyticsTable from 'src/components/views/analytics-v2/display/table';

import { routes } from 'src/routes';
import { buildQueryKey, findExecutionId } from 'src/helpers/analytics-query-execution-id-helper';
import { getUTCDate } from 'src/helpers/date-time-helper';
import { formatNumberInThousand } from 'src/helpers';
import {
  buildCompareDateRange,
  buildDateRangeFromDate,
  displayDateRangeV2
} from 'src/helpers/analytics-helpers';
import {
  RECOMMENDATION_ANALYTIC_METRIC,
  QUERY_EXECUTION_KEY,
  TOP_ITEM_IN_OVERVIEW_RECOMMENDATION,
  TOP_ITEM_IN_DETAIL,
  FEATURES,
  MAX_NUM_X_AXIS_POINTS_RECOMMEND_CHART,
  limitXAxisPoint
} from 'src/constants/analytics';
import * as AnalyticsActions from 'src/stores/actions/analytics-v2-actions';
import * as AnalyticsQueryExecutionIdActions from 'src/stores/actions/analytics-query-execution-id-actions';
import { onAbortApiAnalytic } from 'src/helpers/analytics-v2-helpers';

type Props = {
  performActionSurvey: () => void
};
type ComposedProps = Props & WithI18nProps;
type State = {};

const fromDateStateName = 'recommendationAnalyticsFromDate';
const toDateStateName = 'recommendationAnalyticsToDate';
const compareStateName = 'recommendationAnalyticsCompare';

const getMaxNumOfCharOfValueColumn = (arr: Array, column: string) => {
  let max = _.get(arr, `0[${column}]`, '').length;
  arr.forEach(item => {
    let length = _.get(item, column, '').length;
    if (length > max) {
      max = length;
    }
  });
  return max;
};

class RecommendationAnalyticsOverview extends Component<ComposedProps, State> {
  state = {};

  componentDidMount() {
    const { fromDate, toDate, compare } = this.props;
    if (fromDate && toDate) {
      const fromDateUTC = getUTCDate(fromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(toDate, 24, 0, 0, 0);

      this.fetchAll(fromDateUTC, toDateUTC, compare);
    }
  }

  componentWillReceiveProps(nextProps) {
    const { fromDate, toDate, compare } = this.props;
    const { fromDate: nextFromDate, toDate: nextToDate, compare: nextCompare } = nextProps;

    // Check changing filter
    if (fromDate !== nextFromDate || toDate !== nextToDate || compare !== nextCompare) {
      // Get from date, to date with store timezone
      const fromDateUTC = getUTCDate(nextFromDate, 0, 0, 0);
      const toDateUTC = getUTCDate(nextToDate, 24, 0, 0, 0);

      onAbortApiAnalytic();
      setTimeout(() => {
        this.fetchAll(fromDateUTC, toDateUTC, nextCompare);
      }, 500);
    }

    // Update execution ids
    this.processUpdateExecutionId(this.props, nextProps);
  }

  processHideLandMarks = categories => {
    const landmarksTotalRevenue = document.querySelectorAll(
      '.Rec-Total-revenue .apexcharts-xaxis-tick'
    );
    landmarksTotalRevenue.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksConversionRate = document.querySelectorAll(
      '.Rec-Conversion-rate .apexcharts-xaxis-tick'
    );
    landmarksConversionRate.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
    const landmarksClickRate = document.querySelectorAll('.Rec-Click-rate .apexcharts-xaxis-tick');
    landmarksClickRate.forEach((landmark, index) => {
      if (categories[index] === '') {
        landmark.style.display = 'none';
      }
    });
  };

  render() {
    const {
      fromDate,
      toDate,
      compare,

      recommendationTotalRevenue,
      recommendationTotalRevenueCompared,
      recommendationTotalRevenueLoading,
      recommendationTotalRevenueTotal,
      recommendationTotalRevenuePercentChange,

      recommendationConversionRate,
      recommendationConversionRateCompared,
      recommendationConversionRateLoading,
      recommendationConversionRatePercent,
      recommendationConversionRatePercentChange,

      recommendationClickRate,
      recommendationClickRateCompared,
      recommendationClickRateLoading,
      recommendationClickRatePercent,
      recommendationClickRatePercentChange,

      recommendationTopPagePerformance,
      recommendationTopPagePerformanceLoading,

      recommendationTopWidgetPerformance,
      recommendationTopWidgetPerformanceLoading,

      recommendationTopProductPerformance,
      recommendationTopProductPerformanceLoading,

      performActionSurvey,
      openReviewAddonModal,
      i18n
    } = this.props;
    const displayedSourceDate = displayDateRangeV2(fromDate, toDate);
    const displayedComparedDate = compare
      ? buildCompareDateRange(fromDate, toDate, buildDateRangeFromDate(fromDate, toDate), 'v2')
      : '';

    const {
      series: recommendationTotalRevenueSeries,
      categories: recommendationTotalRevenueCategories
    } = this.buildChartData(
      recommendationTotalRevenue,
      recommendationTotalRevenueCompared,
      displayedSourceDate,
      displayedComparedDate,
      /* customFieldTotal = */ 'total',
      MAX_NUM_X_AXIS_POINTS_RECOMMEND_CHART
    );

    const { series: recommendationClickRateSeries, categories: recommendationClickRateCategories } =
      this.buildChartData(
        recommendationClickRate,
        recommendationClickRateCompared,
        displayedSourceDate,
        displayedComparedDate,
        /* customFieldTotal = */ 'total',
        MAX_NUM_X_AXIS_POINTS_RECOMMEND_CHART
      );

    const {
      series: recommendationConversionRateSeries,
      categories: recommendationConversionRateCategories
    } = this.buildChartData(
      recommendationConversionRate,
      recommendationConversionRateCompared,
      displayedSourceDate,
      displayedComparedDate,
      /* customFieldTotal = */ 'conversionRate',
      MAX_NUM_X_AXIS_POINTS_RECOMMEND_CHART
    );

    setTimeout(() => {
      this.processHideLandMarks(recommendationTotalRevenueCategories);
    }, 1000);

    return (
      <div className="BC-Page BC-Page__RecommendationAnalytics__Overview">
        <Filter
          feature={FEATURES.RECOMMENDATION}
          performActionSurvey={performActionSurvey}
          fromDateStateName={fromDateStateName}
          toDateStateName={toDateStateName}
          compareStateName={compareStateName}
          metric={RECOMMENDATION_ANALYTIC_METRIC.OVERVIEW}
          openReviewAddonModal={openReviewAddonModal}
        />
        <div>
          <Grid columns={{ xs: 1 }}>
            {/* Total revenue generated from recommendation */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}>
              <Chart
                className="Rec-Total-revenue"
                isLimitxAxis
                yAxisTickAmount={3}
                unlimitedYAxis
                type="line"
                suffixOfValue=""
                loading={recommendationTotalRevenueLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendationTotalRevenue_cardTitle'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendation_Tooltip_TotalRevenueGenerated_cardTitle'
                )}
                total={formatNumberInThousand(recommendationTotalRevenueTotal, true)}
                percentGrowthChange={recommendationTotalRevenuePercentChange}
                series={recommendationTotalRevenueSeries}
                categories={recommendationTotalRevenueCategories}
              />
            </Grid.Cell>
            {/* Total revenue generated from recommendation */}

            {/* Conversion rate */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}>
              <Chart
                className="Rec-Conversion-rate"
                isLimitxAxis
                yAxisTickAmount={3}
                type="line"
                suffixOfValue="%"
                loading={recommendationConversionRateLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendationConversionRate_cardTitle'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendation_Tooltip_ConversionRate_cardTitle'
                )}
                total={recommendationConversionRatePercent}
                percentGrowthChange={recommendationConversionRatePercentChange}
                series={recommendationConversionRateSeries}
                categories={recommendationConversionRateCategories}
              />
            </Grid.Cell>
            {/* Conversion rate */}

            {/* Click rate */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}>
              <Chart
                className="Rec-Click-rate"
                isLimitxAxis
                unlimitedYAxis
                yAxisTickAmount={3}
                type="line"
                suffixOfValue="%"
                loading={recommendationClickRateLoading}
                title={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendationClickRate_cardTitle'
                )}
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendation_Tooltip_ClickRate_cardTitle'
                )}
                total={recommendationClickRatePercent}
                percentGrowthChange={recommendationClickRatePercentChange}
                series={recommendationClickRateSeries}
                categories={recommendationClickRateCategories}
              />
            </Grid.Cell>
            {/* Click rate */}

            {/* Top pages */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}>
              <AnalyticsTable
                className="Table__Recommendation__Top--Page"
                style={{
                  '--maxCharacter': getMaxNumOfCharOfValueColumn(
                    recommendationTopPagePerformance
                      .slice(0, TOP_ITEM_IN_OVERVIEW_RECOMMENDATION)
                      .map((item, index) => {
                        return {
                          revenue: formatNumberInThousand(item.totalRevenue, true)
                        };
                      }),
                    'revenue'
                  )
                }}
                loading={recommendationTopPagePerformanceLoading}
                smallRow
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendation_Tooltip_TopPage_cardTitle'
                )}
                title={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendationTopPagePerformance_cardTitle'
                )}
                linkDetail={routes.analytics.recommendationPagePerformance()}
                dataTableProps={{
                  headings: [
                    i18n.translate(
                      'AdminHTML.Analytics.RecommendationAnalytics.recommendationPagePerformance_header_page'
                    ),
                    i18n.translate(
                      'AdminHTML.Analytics.RecommendationAnalytics.recommendationPagePerformance_header_revenue'
                    )
                  ],
                  rows: this.buildFallbackDataAnalyticsTable(
                    recommendationTopPagePerformance
                      .slice(0, TOP_ITEM_IN_OVERVIEW_RECOMMENDATION)
                      .map((item, index) => [
                        item.page,
                        formatNumberInThousand(item.totalRevenue, true)
                      ])
                  ),
                  columnContentTypes: ['text', 'numeric'],
                  // 2 props is used to hide scroll for the table
                  hideScrollIndicator: true,
                  truncate: true
                }}
              />
            </Grid.Cell>
            {/* Top pages */}

            {/* Top widget */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}>
              <AnalyticsTable
                className="Table__Recommendation__Top--Widget"
                style={{
                  '--maxCharacter': getMaxNumOfCharOfValueColumn(
                    recommendationTopWidgetPerformance
                      .slice(0, TOP_ITEM_IN_OVERVIEW_RECOMMENDATION)
                      .map((item, index) => {
                        return {
                          revenue: `${formatNumberInThousand(item.totalRevenue)}`
                        };
                      }),
                    'revenue'
                  )
                }}
                loading={recommendationTopWidgetPerformanceLoading}
                smallRow
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendation_Tooltip_TopWidget_cardTitle'
                )}
                title={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendationTopWidgetPerformance_cardTitle'
                )}
                linkDetail={routes.analytics.recommendationWidgetPerformance()}
                dataTableProps={{
                  headings: [
                    i18n.translate(
                      'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_widget'
                    ),
                    i18n.translate(
                      'AdminHTML.Analytics.RecommendationAnalytics.recommendationWidgetPerformance_header_revenue'
                    )
                  ],
                  rows: this.buildFallbackDataAnalyticsTable(
                    recommendationTopWidgetPerformance
                      .slice(0, TOP_ITEM_IN_OVERVIEW_RECOMMENDATION)
                      .map((item, index) => [
                        `${item.widgetName} (${item.widgetId})`,
                        formatNumberInThousand(item.totalRevenue, true)
                      ])
                  ),
                  columnContentTypes: ['text', 'numeric'],
                  // 2 props is used to hide scroll for the table
                  hideScrollIndicator: true,
                  truncate: true
                }}
              />
            </Grid.Cell>
            {/* Top widget */}

            {/* Top product */}
            <Grid.Cell columnSpan={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 4 }}>
              <AnalyticsTable
                className="Table__Recommendation__Top--Product"
                style={{
                  '--maxCharacter': getMaxNumOfCharOfValueColumn(
                    recommendationTopProductPerformance
                      .slice(0, TOP_ITEM_IN_OVERVIEW_RECOMMENDATION)
                      .map((item, index) => {
                        return {
                          revenue: formatNumberInThousand(item.revenue, true)
                        };
                      }),
                    'revenue'
                  )
                }}
                loading={recommendationTopProductPerformanceLoading}
                smallRow
                tooltip={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendation_Tooltip_TopProduct_cardTitle'
                )}
                title={i18n.translate(
                  'AdminHTML.Analytics.RecommendationAnalytics.recommendationTopProductPerformance_cardTitle'
                )}
                linkDetail={routes.analytics.recommendationProductPerformance()}
                dataTableProps={{
                  headings: [
                    i18n.translate(
                      'AdminHTML.Analytics.RecommendationAnalytics.recommendationProductPerformance_header_product'
                    ),
                    i18n.translate(
                      'AdminHTML.Analytics.RecommendationAnalytics.recommendationProductPerformance_header_total_revenue'
                    )
                  ],
                  rows: this.buildFallbackDataAnalyticsTable(
                    recommendationTopProductPerformance
                      .slice(0, TOP_ITEM_IN_OVERVIEW_RECOMMENDATION)
                      .map((item, index) => [
                        item.productName,
                        formatNumberInThousand(item.revenue, true)
                      ])
                  ),
                  columnContentTypes: ['text', 'numeric'],
                  // 2 props is used to hide scroll for the table
                  hideScrollIndicator: true,
                  truncate: true
                }}
              />
            </Grid.Cell>
            {/* Top product */}
          </Grid>
        </div>
      </div>
    );
  }

  buildChartData = (
    sourceDataArr,
    comparedDataArr = [],
    displayedSourceDate,
    displayedComparedDate,
    customFieldTotal, // Priority field here to get data for showing on chart,
    isLimitxAxis
  ) => {
    const { compare, timezone } = this.props;

    let categories = sourceDataArr.map(item => {
      // Parse UTC time into tenant's timezone to format datetime showed on chart
      return new Date(item.datetime).toLocaleString('en-US', { timeZone: timezone });
    });

    // Process limit x axis max 3,4 points
    if (isLimitxAxis && isLimitxAxis < categories.length) {
      categories = limitXAxisPoint(categories, MAX_NUM_X_AXIS_POINTS_RECOMMEND_CHART);
    }

    const fullCategories = sourceDataArr.map(item => {
      // Parse UTC time into tenant's timezone to format datetime showed on chart
      return new Date(item.datetime).toLocaleString('en-US', { timeZone: timezone });
    });

    const data = sourceDataArr.map(item =>
      customFieldTotal ? item[customFieldTotal] : item.total
    );

    const series = [
      {
        name: displayedSourceDate,
        data: data,
        fullCategories
      }
    ];

    if (compare) {
      const comparedData = comparedDataArr.map(item =>
        customFieldTotal ? item[customFieldTotal] : item.total
      );
      series.push({
        name: displayedComparedDate,
        data: comparedData
      });
    }

    return {
      series,
      categories
    };
  };

  buildFallbackDataAnalyticsTable = (builtData: any[]) => {
    if (_.isEmpty(builtData)) {
      return [...Array(TOP_ITEM_IN_OVERVIEW_RECOMMENDATION)].map((item, index) => ['N/A', 0]);
    }
    return builtData;
  };

  fetchAll = (fromDate, toDate, compare) => {
    const {
      recommendationTotalRevenueLoading,
      recommendationConversionRateLoading,
      recommendationClickRateLoading,
      recommendationTopPagePerformanceLoading,
      recommendationTopWidgetPerformanceLoading,
      recommendationTopProductPerformanceLoading
    } = this.props;

    if (!recommendationTotalRevenueLoading) {
      this.getRecommendationTotalRevenueGenerated(fromDate, toDate, compare);
    }

    if (!recommendationConversionRateLoading) {
      this.getRecommendationConversionRate(fromDate, toDate, compare);
    }

    if (!recommendationClickRateLoading) {
      this.getRecommendationClickRate(fromDate, toDate, compare);
    }

    if (!recommendationTopPagePerformanceLoading) {
      this.getRecommendationTopPage(fromDate, toDate, compare);
    }

    if (!recommendationTopWidgetPerformanceLoading) {
      this.getRecommendationTopWidget(fromDate, toDate, compare);
    }

    if (!recommendationTopProductPerformanceLoading) {
      this.getRecommendationTopProduct(fromDate, toDate, compare);
    }
  };

  getRecommendationTopPage = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // TODO: remove this line when there's any update later
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.RECOMMENDATION_TOP_PAGE_PERFORMANCE;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getRecommendationTopPage(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  getRecommendationTopWidget = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // TODO: remove this line when there's any update later
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.RECOMMENDATION_TOP_WIDGET_PERFORMANCE;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getRecommendationTopWidget(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  getRecommendationTopProduct = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // TODO: remove this line when there's any update later
    compare = false;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    const queryExecutionKey = QUERY_EXECUTION_KEY.RECOMMENDATION_TOP_PRODUCT_PERFORMANCE;
    // Get execution id if existing
    const queryExecutionId = findExecutionId(queryKey, queryExecutionKey, executionIds);

    this.props.getRecommendationTopProduct(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      TOP_ITEM_IN_DETAIL
    );
  };

  getRecommendationTotalRevenueGenerated = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.RECOMMENDATION_TOTAL_REVENUE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.RECOMMENDATION_TOTAL_REVENUE_COMPARED,
      executionIds
    );

    this.props.getRecommendationTotalRevenueGenerated(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getRecommendationConversionRate = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.RECOMMENDATION_CONVERSION_RATE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.RECOMMENDATION_CONVERSION_RATE_COMPARED,
      executionIds
    );

    this.props.getRecommendationConversionRate(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  getRecommendationClickRate = (fromDate, toDate, compare) => {
    const { executionIds } = this.props;

    // Build query key
    const queryKey = buildQueryKey({ fromDate, toDate, compare });
    // Get execution id if existing
    const queryExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.RECOMMENDATION_CLICK_RATE,
      executionIds
    );

    const comparedExecutionId = findExecutionId(
      queryKey,
      QUERY_EXECUTION_KEY.RECOMMENDATION_CLICK_RATE_COMPARED,
      executionIds
    );

    this.props.getRecommendationClickRate(
      fromDate,
      toDate,
      compare,
      queryExecutionId,
      comparedExecutionId
    );
  };

  processUpdateExecutionId = (props, nextProps) => {
    this.processTopPagePerformanceExecutionId(props, nextProps);
    this.processTopWidgetPerformanceExecutionId(props, nextProps);
    this.processTopProductPerformanceExecutionId(props, nextProps);
    this.processTotalRevenueExecutionId(props, nextProps);
    this.processConversionRateExecutionId(props, nextProps);
    this.processClickRateExecutionId(props, nextProps);
  };

  processTopPagePerformanceExecutionId = (props, nextProps) => {
    const { recommendationTopPagePerformanceQueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      recommendationTopPagePerformanceQueryExecutionId:
        recommendationTopPagePerformanceQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(recommendationTopPagePerformanceQueryExecutionIdNext) &&
      recommendationTopPagePerformanceQueryExecutionId !==
        recommendationTopPagePerformanceQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_TOP_PAGE_PERFORMANCE,
        executionId: recommendationTopPagePerformanceQueryExecutionIdNext
      });
    }
  };

  processTopWidgetPerformanceExecutionId = (props, nextProps) => {
    const { recommendationTopWidgetPerformanceQueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      recommendationTopWidgetPerformanceQueryExecutionId:
        recommendationTopWidgetPerformanceQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(recommendationTopWidgetPerformanceQueryExecutionIdNext) &&
      recommendationTopWidgetPerformanceQueryExecutionId !==
        recommendationTopWidgetPerformanceQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_TOP_WIDGET_PERFORMANCE,
        executionId: recommendationTopWidgetPerformanceQueryExecutionIdNext
      });
    }
  };

  processTopProductPerformanceExecutionId = (props, nextProps) => {
    const { recommendationTopProductPerformanceQueryExecutionId } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      recommendationTopProductPerformanceQueryExecutionId:
        recommendationTopProductPerformanceQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(recommendationTopProductPerformanceQueryExecutionIdNext) &&
      recommendationTopProductPerformanceQueryExecutionId !==
        recommendationTopProductPerformanceQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_TOP_PRODUCT_PERFORMANCE,
        executionId: recommendationTopProductPerformanceQueryExecutionIdNext
      });
    }
  };

  processTotalRevenueExecutionId = (props, nextProps) => {
    const {
      recommendationTotalRevenueQueryExecutionId,
      recommendationTotalRevenueComparedQueryExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      recommendationTotalRevenueQueryExecutionId: recommendationTotalRevenueQueryExecutionIdNext,
      recommendationTotalRevenueComparedQueryExecutionId:
        recommendationTotalRevenueComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(recommendationTotalRevenueQueryExecutionIdNext) &&
      recommendationTotalRevenueQueryExecutionId !== recommendationTotalRevenueQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_TOTAL_REVENUE,
        executionId: recommendationTotalRevenueQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(recommendationTotalRevenueComparedQueryExecutionIdNext) &&
      recommendationTotalRevenueComparedQueryExecutionId !==
        recommendationTotalRevenueComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_TOTAL_REVENUE_COMPARED,
        executionId: recommendationTotalRevenueComparedQueryExecutionIdNext
      });
    }
  };

  processConversionRateExecutionId = (props, nextProps) => {
    const {
      recommendationConversionRateQueryExecutionId,
      recommendationConversionRateComparedQueryExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      recommendationConversionRateQueryExecutionId:
        recommendationConversionRateQueryExecutionIdNext,
      recommendationConversionRateComparedQueryExecutionId:
        recommendationConversionRateComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(recommendationConversionRateQueryExecutionIdNext) &&
      recommendationConversionRateQueryExecutionId !==
        recommendationConversionRateQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_CONVERSION_RATE,
        executionId: recommendationConversionRateQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(recommendationConversionRateComparedQueryExecutionIdNext) &&
      recommendationConversionRateComparedQueryExecutionId !==
        recommendationConversionRateComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_CONVERSION_RATE_COMPARED,
        executionId: recommendationConversionRateComparedQueryExecutionIdNext
      });
    }
  };

  processClickRateExecutionId = (props, nextProps) => {
    const {
      recommendationClickRateQueryExecutionId,
      recommendationClickRateComparedQueryExecutionId
    } = props;

    const {
      fromDate: nextFromDate,
      toDate: nextToDate,
      compare: nextCompare,
      recommendationClickRateQueryExecutionId: recommendationClickRateQueryExecutionIdNext,
      recommendationClickRateComparedQueryExecutionId:
        recommendationClickRateComparedQueryExecutionIdNext
    } = nextProps;

    // Build query key
    const queryKey = buildQueryKey({
      fromDate: getUTCDate(nextFromDate, 0, 0, 0),
      toDate: getUTCDate(nextToDate, 24, 0, 0),
      compare: nextCompare
    });

    // Update execution id
    if (
      !_.isEmpty(recommendationClickRateQueryExecutionIdNext) &&
      recommendationClickRateQueryExecutionId !== recommendationClickRateQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_CLICK_RATE,
        executionId: recommendationClickRateQueryExecutionIdNext
      });
    }

    if (
      !_.isEmpty(recommendationClickRateComparedQueryExecutionIdNext) &&
      recommendationClickRateComparedQueryExecutionId !==
        recommendationClickRateComparedQueryExecutionIdNext
    ) {
      this.props.updateExecutionId({
        queryKey,
        executionIdKey: QUERY_EXECUTION_KEY.RECOMMENDATION_CLICK_RATE_COMPARED,
        executionId: recommendationClickRateComparedQueryExecutionIdNext
      });
    }
  };
}

const mapDispatchToProps = (dispatch, ownProps) => ({
  changeValue: (name, value) => dispatch(AnalyticsActions.changeValue(name, value)),

  getRecommendationTotalRevenueGenerated: (
    from,
    to,
    compare,
    queryExecutionId,
    comparedExecutionId
  ) =>
    dispatch(
      AnalyticsActions.getRecommendationTotalRevenueGenerated(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),
  getRecommendationConversionRate: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getRecommendationConversionRate(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),
  getRecommendationClickRate: (from, to, compare, queryExecutionId, comparedExecutionId) =>
    dispatch(
      AnalyticsActions.getRecommendationClickRate(
        from,
        to,
        compare,
        queryExecutionId,
        comparedExecutionId,
        ownProps.i18n
      )
    ),
  getRecommendationTopPage: (fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getRecommendationTopPage(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),
  getRecommendationTopWidget: (fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getRecommendationTopWidget(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),
  getRecommendationTopProduct: (fromDate, toDate, compare, queryExecutionId, top) =>
    dispatch(
      AnalyticsActions.getRecommendationTopProduct(
        fromDate,
        toDate,
        compare,
        queryExecutionId,
        top,
        ownProps.i18n
      )
    ),
  updateExecutionId: data => dispatch(AnalyticsQueryExecutionIdActions.updateExecutionId(data))
});

const mapStateToProps = (state, props) => {
  return {
    moneyFormat: _.get(state.oauth, 'tenantApp.externalInfo.money_format', ''),
    timezone: _.get(state.oauth, 'tenantApp.externalInfo.iana_timezone', ''),
    fromDate: _.get(state.analyticsV2, fromDateStateName),
    toDate: _.get(state.analyticsV2, toDateStateName),
    compare: _.get(state.analyticsV2, compareStateName, false),

    // Query execution ids
    executionIds: _.get(state.analyticsQueryExecutionId, 'list', []),

    // Recommendation total revenue
    recommendationTotalRevenue: _.get(state.analyticsV2, 'recommendationTotalRevenue', []),
    recommendationTotalRevenueCompared: _.get(
      state.analyticsV2,
      'recommendationTotalRevenueCompared',
      []
    ),
    recommendationTotalRevenueLoading: _.get(
      state.analyticsV2,
      'recommendationTotalRevenueLoading',
      false
    ),
    recommendationTotalRevenueTotal: _.get(
      state.analyticsV2,
      'recommendationTotalRevenueTotal',
      ''
    ),
    recommendationTotalRevenuePercentChange: _.get(
      state.analyticsV2,
      'recommendationTotalRevenuePercentChange',
      0
    ),
    recommendationTotalRevenueQueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationTotalRevenueQueryExecutionId'
    ),
    recommendationTotalRevenueComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationTotalRevenueComparedQueryExecutionId'
    ),

    // Recommendation conversion rate
    recommendationConversionRate: _.get(state.analyticsV2, 'recommendationConversionRate', []),
    recommendationConversionRateCompared: _.get(
      state.analyticsV2,
      'recommendationConversionRateCompared',
      []
    ),
    recommendationConversionRateLoading: _.get(
      state.analyticsV2,
      'recommendationConversionRateLoading',
      false
    ),
    recommendationConversionRatePercent: _.get(
      state.analyticsV2,
      'recommendationConversionRatePercent',
      ''
    ),
    recommendationConversionRatePercentChange: _.get(
      state.analyticsV2,
      'recommendationConversionRatePercentChange',
      0
    ),
    recommendationConversionRateQueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationConversionRateQueryExecutionId'
    ),
    recommendationConversionRateComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationConversionRateComparedQueryExecutionId'
    ),

    // Recommendation click rate
    recommendationClickRate: _.get(state.analyticsV2, 'recommendationClickRate', []),
    recommendationClickRateCompared: _.get(
      state.analyticsV2,
      'recommendationClickRateCompared',
      []
    ),
    recommendationClickRateLoading: _.get(
      state.analyticsV2,
      'recommendationClickRateLoading',
      false
    ),
    recommendationClickRatePercent: _.get(state.analyticsV2, 'recommendationClickRatePercent', ''),
    recommendationClickRatePercentChange: _.get(
      state.analyticsV2,
      'recommendationClickRatePercentChange',
      0
    ),
    recommendationClickRateQueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationClickRateQueryExecutionId'
    ),
    recommendationClickRateComparedQueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationClickRateComparedQueryExecutionId'
    ),

    // Recommendation: top page
    recommendationTopPagePerformance: _.get(
      state.analyticsV2,
      'recommendationTopPagePerformance',
      []
    ),
    recommendationTopPagePerformanceLoading: _.get(
      state.analyticsV2,
      'recommendationTopPagePerformanceLoading',
      false
    ),
    recommendationTopPagePerformanceQueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationTopPagePerformanceQueryExecutionId'
    ),

    // Recommendation: top filter option value
    recommendationTopWidgetPerformance: _.get(
      state.analyticsV2,
      'recommendationTopWidgetPerformance',
      []
    ),
    recommendationTopWidgetPerformanceLoading: _.get(
      state.analyticsV2,
      'recommendationTopWidgetPerformanceLoading',
      false
    ),
    recommendationTopWidgetPerformanceQueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationTopWidgetPerformanceQueryExecutionId'
    ),

    // Recommendation: top product
    recommendationTopProductPerformance: _.get(
      state.analyticsV2,
      'recommendationTopProductPerformance',
      []
    ),
    recommendationTopProductPerformanceLoading: _.get(
      state.analyticsV2,
      'recommendationTopProductPerformanceLoading',
      false
    ),
    recommendationTopProductPerformanceQueryExecutionId: _.get(
      state.analyticsV2,
      'recommendationTopProductPerformanceQueryExecutionId'
    )
  };
};

export default withI18n()(
  connect(mapStateToProps, mapDispatchToProps)(RecommendationAnalyticsOverview)
);
